import React from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, User, Send } from "lucide-react";

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = React.useState({ name: "", email: "" });
  const [errors, setErrors] = React.useState({});

  const onChange = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!values.name.trim()) next.name = t("newsletter.error_name");
    if (!validEmail(values.email)) next.email = t("newsletter.error_email");
    setErrors(next);
    if (Object.keys(next).length === 0) {
      navigate("/thank_you");
    }
  };

  return (
    <div
      id="newsletter"
      data-testid="newsletter-cta"
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#059669] to-[#047857] p-6 sm:p-10 lg:p-14 shadow-2xl ring-1 ring-emerald-400/30"
    >
      <span
        aria-hidden
        className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/5"
      />
      <span
        aria-hidden
        className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-white/5"
      />

      <div className="relative grid gap-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6 text-white">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">
            <Send size={12} />
            {t("newsletter.eyebrow")}
          </span>
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            {t("newsletter.title")}
          </h3>
          <p className="mt-4 text-base sm:text-lg text-emerald-50/95 leading-relaxed max-w-xl">
            {t("newsletter.subtitle")}
          </p>
        </div>

        <form
          data-testid="newsletter-form"
          onSubmit={onSubmit}
          noValidate
          className="lg:col-span-6 rounded-2xl bg-white p-6 sm:p-8 shadow-xl"
        >
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="newsletter-name"
                className="text-sm font-semibold text-slate-900"
              >
                {t("newsletter.name_label")}
              </Label>
              <div className="relative mt-1.5">
                <User
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="newsletter-name"
                  data-testid="newsletter-name"
                  value={values.name}
                  onChange={onChange("name")}
                  placeholder={t("newsletter.name_placeholder")}
                  className="pl-9 h-12 bg-white text-slate-900 placeholder:text-slate-400 border-slate-300"
                  aria-invalid={!!errors.name}
                />
              </div>
              {errors.name && (
                <p
                  className="mt-1 text-xs text-red-600"
                  data-testid="error-name"
                >
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="newsletter-email"
                className="text-sm font-semibold text-slate-900"
              >
                {t("newsletter.email_label")}
              </Label>
              <div className="relative mt-1.5">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="newsletter-email"
                  data-testid="newsletter-email"
                  type="email"
                  value={values.email}
                  onChange={onChange("email")}
                  placeholder={t("newsletter.email_placeholder")}
                  className="pl-9 h-12 bg-white text-slate-900 placeholder:text-slate-400 border-slate-300"
                  aria-invalid={!!errors.email}
                />
              </div>
              {errors.email && (
                <p
                  className="mt-1 text-xs text-red-600"
                  data-testid="error-email"
                >
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            data-testid="newsletter-submit"
            className="mt-6 h-12 w-full bg-[#0F2942] hover:bg-[#1E3A5F] text-white rounded-full text-base font-semibold"
          >
            {t("newsletter.submit")}
          </Button>

          <p className="mt-3 text-xs text-slate-500">
            {t("newsletter.privacy_note")}
          </p>
        </form>
      </div>
    </div>
  );
}
